<template>
  <div class="d-flex flex-column">
    <div class="row justify-content-between mb-4">
      <div class="col-12 col-sm-4 mb-3">
        <BarChart
          v-if="!getLoading"
          :labels="tokensPerPartyIdLabels"
          :datasets="tokensPerPartyIdDatasets"
          :title="$t('sections.tmLandingPage.graphs.tokensPerParty')"
        />
      </div>
      <div class="col-12 col-sm-4 mb-3">
        <PieChart
          v-if="!getLoading"
          :labels="tokensByStatusLabels"
          :datasets="tokensByStatusDatasets"
          :title="$t('sections.tmLandingPage.graphs.tokensActive')"
        />
      </div>
      <div class="col-12 col-sm-4 mb-3">
        <BarChart
          v-if="!getLoading"
          :labels="historiesByDateLabels"
          :datasets="tokensSuccessByWeekDatasets"
          :title="$t('sections.tmLandingPage.graphs.tokensSuccessError')"
        />
      </div>
    </div>
    <span class="section-title">
      {{ $t("sections.tmLandingPage.table.title") }}
    </span>
    <PaginationWrapper
      :page="page"
      :totalCount="totalCount"
      @onPageChange="handlePageChange"
      export
      @exportTable="exportList"
    >
      <div slot="table">
        <base-table :data="getHistory">
          <el-table-column
            prop="party_id"
            :label="$t('sections.tmLandingPage.table.partyId')"
          />
          <el-table-column
            prop="uid"
            :label="$t('sections.tmLandingPage.table.uid')"
          />
          <el-table-column
            prop="action"
            :label="$t('sections.tmLandingPage.table.action')"
          />
          <el-table-column
            prop="status"
            :label="$t('sections.tmLandingPage.table.status')"
          />
          <el-table-column :label="$t('sections.tmLandingPage.table.date')">
            <template slot-scope="props">
              {{ props.row.date ? formatTime(props.row.date) : props.row.date }}
            </template>
          </el-table-column>
          <el-table-column
            fixed="right"
            width="60"
            v-if="tokensHistoryPermissions.read"
          >
            <template slot-scope="props">
              <div class="d-flex justify-content-center">
                <el-dropdown trigger="click">
                  <div class="outline-icon-button">
                    <font-awesome-icon icon="fa-regular fa-ellipsis-vertical" />
                  </div>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item>
                      <router-link
                        :to="{
                          name: 'Token Error',
                          params: {
                            id: props.row.id,
                            error: props.row,
                          },
                        }"
                      >
                        {{ $t("sections.tokens.options")[0] }}
                      </router-link>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </template>
          </el-table-column>
        </base-table>
      </div>
    </PaginationWrapper>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import PaginationWrapper from '@/components/sections/common/PaginationWrapper.vue';
import BarChart from '@/components/ui/charts/BarChart.vue';
import PieChart from '@/components/ui/charts/PieChart.vue';
import { formatTime } from '@/utils';

export default {
  name: 'TokensManagerLandingPage',
  components: {
    PaginationWrapper,
    BarChart,
    PieChart,
  },
  data() {
    return {
      activeTab: 0,
      tokensPerPartyIdLabels: [],
      tokensCountByPartyId: [],
      tokensByStatusLabels: [],
      tokensCountByStatus: [],
      historiesByDateLabels: [],
      historiesByDateSuccesses: [],
      historiesByDateErrors: [],
      page: 1,
      totalCount: 0,
    };
  },
  computed: {
    ...mapGetters(['getLoading', 'getHistory', 'getPagePermissions']),
    tokensHistoryPermissions() {
      return this.getPagePermissions('Tokens Manager - History');
    },
    tokensPerPartyIdDatasets() {
      return [
        {
          label: this.$t('sections.tmLandingPage.graphs.tokensPerParty'),
          borderColor: '#1a84cf',
          backgroundColor: 'rgba(26, 132, 207, 0.5)',
          data: this.tokensCountByPartyId,
        },
      ];
    },
    tokensByStatusDatasets() {
      return [
        {
          backgroundColor: ['#1a84cf', '#202426'],
          borderColor: ['#1a84cf', '#202426'],
          data: this.tokensCountByStatus,
        },
      ];
    },
    tokensSuccessByWeekDatasets() {
      return [
        {
          label: this.$t('sections.tmLandingPage.graphs.tokensSuccess'),
          borderColor: '#1a84cf',
          backgroundColor: 'rgba(26, 132, 207, 0.5)',
          data: this.historiesByDateSuccesses,
        },
        {
          label: this.$t('sections.tmLandingPage.graphs.tokensError'),
          borderColor: '#202426',
          backgroundColor: 'rgba(32, 36, 38, 0.5)',
          data: this.historiesByDateErrors,
        },
      ];
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchCountByPartyIdData() {
      try {
        const countByPartyId = await this.$store.dispatch(
          'getTokenCountByField',
          { field: 'party_id' },
        );

        this.tokensCountByPartyId = countByPartyId.count;
        this.tokensPerPartyIdLabels = countByPartyId.labels;
      } catch (e) {
        console.log("Couldn't get tokens count by party id.");
      }
    },
    async fetchCountByStatusData() {
      try {
        const countByStatus = await this.$store.dispatch(
          'getTokenCountByField',
          {
            field: 'valid',
          },
        );

        this.tokensCountByStatus = countByStatus.count;
        this.tokensByStatusLabels = countByStatus.labels.map((label) => (label ? 'Active' : 'Inactive'));
      } catch (e) {
        console.log("Couldn't get tokens count by status.");
      }
    },
    async fetchHistoriesSuccessByDate(dateFrom, datesSuccessMap) {
      try {
        const historiesSuccessByDate = await this.$store.dispatch(
          'getHistoriesCountByField',
          {
            field: 'date',
            query: { action: 'CREATE', status: 'SUCCESS', dateFrom },
          },
        );

        await Promise.all(
          historiesSuccessByDate.labels.map(async (label, index) => {
            datesSuccessMap[label.slice(0, 10)] = historiesSuccessByDate.count[index];
          }),
        );

        this.historiesByDateSuccesses = Object.values(datesSuccessMap);
      } catch (e) {
        console.log("Couldn't get tokens history by date.");
      }
    },
    async fetchHistoriesErrorByDate(dateFrom, datesErrorMap) {
      try {
        const historiesErrorByDate = await this.$store.dispatch(
          'getHistoriesCountByField',
          {
            field: 'date',
            query: { action: 'CREATE', status: 'ERROR', dateFrom },
          },
        );

        await Promise.all(
          historiesErrorByDate.labels.map(async (label, index) => {
            datesErrorMap[label.slice(0, 10)] = historiesErrorByDate.count[index];
          }),
        );
        this.historiesByDateErrors = Object.values(datesErrorMap);
      } catch (e) {
        console.log("Couldn't get tokens history errors by date.");
      }
    },
    async fetchTokensErrorHistory() {
      this.totalCount = await this.$store.dispatch('storeHistory', {
        query: {
          status: 'ERROR',
          date: new Date(new Date().getTime() - 24 * 60 * 60 * 1000),
        },
        page: this.page,
      });
    },
    async fetchData() {
      try {
        this.$store.dispatch('storeLoading', true);

        const dateFrom = new Date();
        dateFrom.setDate(dateFrom.getDate() - 6);
        dateFrom.setUTCHours(0, 0, 0, 0);

        const datesLabel = [];
        const datesSuccessMap = {};
        const datesErrorMap = {};

        await Promise.all(
          [...Array(7)].map(async (_, i) => {
            const d = new Date();
            d.setDate(d.getDate() - i);
            datesLabel.push(d.toLocaleString('en-UK', { weekday: 'long' }));
            datesSuccessMap[d.toISOString().slice(0, 10)] = 0;
            datesErrorMap[d.toISOString().slice(0, 10)] = 0;
          }),
        );
        this.historiesByDateLabels = datesLabel;

        const countByPartyIdPromise = this.fetchCountByPartyIdData();
        const countByStatusPromise = this.fetchCountByStatusData();
        const historiesSuccessByDatePromise = this.fetchHistoriesSuccessByDate(
          dateFrom,
          datesSuccessMap,
        );
        const historiesErrorByDatePromise = this.fetchHistoriesErrorByDate(
          dateFrom,
          datesErrorMap,
        );
        const historyPromise = this.fetchTokensErrorHistory();

        await Promise.allSettled([
          countByPartyIdPromise,
          countByStatusPromise,
          historiesSuccessByDatePromise,
          historiesErrorByDatePromise,
          historyPromise,
        ]);

        this.$store.dispatch('storeLoading', false);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('storeLoading', false);
      }
    },
    async handlePageChange(page) {
      this.page = page;
      await this.fetchData();
    },
    async exportList() {
      await this.$store.dispatch('storeLoading', true);

      try {
        const file = await this.$store.dispatch('exportList', {
          query: this.query,
          type: 'tokensHistory',
        });

        if (file.data === '') {
          this.$message.error(
            this.$t('components.alerts.exportFileError', {
              type: 'tokensHistory',
            }),
          );
        } else {
          const fileURL = window.URL.createObjectURL(new Blob([file.data]));
          const fileLink = document.createElement('a');

          fileLink.href = fileURL;
          fileLink.setAttribute(
            'download',
            `tokensHistory-${new Date()
              .toISOString()
              .slice(0, 10)
              .replaceAll('-', '')}.csv`,
          );
          document.body.appendChild(fileLink);

          fileLink.click();
          this.$message.success(
            this.$t('components.alerts.exportSuccess', {
              type: 'tokensHistory',
            }),
          );
        }
      } catch (e) {
        this.$message.error(
          this.$t('components.alerts.exportError', { type: 'tokensHistory' }),
        );
        console.log(e);
      }

      await this.$store.dispatch('storeLoading', false);
    },
    formatTime,
  },
};
</script>
